/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	PostEffect,
	RenderTarget,
	Shader,
	Texture,
	PIXELFORMAT_R8_G8_B8_A8,
	FILTER_NEAREST,
	ADDRESS_CLAMP_TO_EDGE,
	Vec4,
} from "playcanvas";
import type { AppBase, CameraComponent, GraphicsDevice } from "playcanvas";
import { BLEND_SHADER, COPY_SHADER } from "./shaders/accumulation";
import { createQuadShader } from "./shaders/quad-shader";

export type ProgressiveAAOptions = {
	/** Number of jitter samples to accumulate. Default: 8. */
	samples?: number;
};

/**
 * Progressive anti-aliasing for static scenes.
 *
 * Produces a supersampled result at the current canvas resolution by
 * rendering multiple frames with sub-pixel projection jitter (Halton 2,3
 * sequence) and incrementally blending them together. No canvas resize or
 * additional full-size framebuffer is needed, only two screen-sized
 * ping-pong render targets for the accumulation.
 *
 * Lifecycle is driven entirely by the PlayCanvas app's `autoRender` flag:
 *
 * - When `autoRender` transitions from `true` to `false` (i.e. continuous
 *   rendering stops, typically because camera motion or interaction ended),
 *   accumulation starts automatically.
 * - While accumulating, the class requests additional frames via
 *   `app.renderNextFrame`, applying a different sub-pixel jitter offset to
 *   the camera projection matrix on each one.
 * - Once all samples have been collected, a final clean (un-jittered) frame
 *   is rendered. The accumulated result then remains on screen for as long
 *   as `autoRender` stays `false`, no further frames are rendered.
 * - If `autoRender` turns back on (e.g. the user starts interacting again),
 *   accumulation is cancelled and the post-effect is removed so that normal
 *   rendering resumes without interference.
 *
 * This means consumers only need to construct the instance and later call
 * `destroy()` when it is no longer needed. Starting and stopping is handled
 * internally.
 */
export class ProgressiveAA {
	private _app: AppBase;
	private _cameraComponent: CameraComponent;
	private _effect: AccumulationEffect;
	private _sampleCount: number;
	private _currentSample = 0;
	private _active = false;
	private _hadAutoRender = false;

	constructor(
		app: AppBase,
		cameraComponent: CameraComponent,
		options?: ProgressiveAAOptions,
	) {
		this._app = app;
		this._cameraComponent = cameraComponent;
		this._sampleCount = options?.samples ?? 8;
		this._effect = new AccumulationEffect(app.graphicsDevice, this._onResize);

		// Always watch for renders so we can auto-start/restart accumulation
		this._app.on("framerender", this._onFrameRender);
	}

	get isActive(): boolean {
		return this._active;
	}

	/**
	 * Release GPU resources.
	 */
	destroy(): void {
		this._stop();
		this._app.off("framerender", this._onFrameRender);
		this._effect.destroy();
	}

	private _start(): void {
		if (this._active) {
			return;
		}
		this._active = true;
		this._currentSample = 0;
		this._cameraComponent.postEffects.addEffect(this._effect as any);
	}

	private _stop(): void {
		if (!this._active) {
			return;
		}
		this._active = false;
		this._clearJitter();

		this._cameraComponent.postEffects.removeEffect(this._effect as any);
	}

	private _onFrameRender = () => {
		const { autoRender } = this._app;
		const stop = autoRender && this._active;
		const start = !autoRender && this._hadAutoRender;

		if (stop) {
			this._stop();
		} else if (start) {
			this._start();
		}

		this._hadAutoRender = autoRender;

		if (!this._active) {
			return;
		}

		const sampleIndex = this._currentSample;

		if (sampleIndex < this._sampleCount) {
			// Incremental averaging: blend factor = i / (i + 1)
			// Sample 0: factor 0 → 100% current, Sample 7: factor 7/8 → 87.5% accum
			this._effect.blendFactor = sampleIndex / (sampleIndex + 1);
			this._applyJitter(this._currentSample);
		} else if (sampleIndex === this._sampleCount) {
			// All samples done, render one final clean frame without jitter,
			// showing 100% of the accumulated result
			this._clearJitter();
			this._effect.blendFactor = 1.0;
		}

		if (sampleIndex <= this._sampleCount) {
			this._currentSample++;
			this._app.renderNextFrame = true;
		}
	};

	private _onResize = () => {
		// When rendertargets are resized, we need to restart the sampling (if active)
		// since the rendertargets will have lost any accumulated data.
		this._currentSample = 0;
	};

	private _applyJitter(sampleIndex: number) {
		const { camera } = this._cameraComponent;
		camera._calculateProjection = this._createJitterCallback(sampleIndex);
	}

	private _createJitterCallback(sampleIndex: number) {
		// Halton(2,3) sequence gives well-distributed sub-pixel offsets
		const jitterX = halton(sampleIndex + 1, 2) - 0.5;
		const jitterY = halton(sampleIndex + 1, 3) - 0.5;
		const device = this._app.graphicsDevice;

		const { camera } = this._cameraComponent;

		const callback = (projMat: { data: Float32Array }, _view: number) => {
			// Temporarily clear the override so the default projection is computed
			camera._calculateProjection = null;
			camera._projMatDirty = true;

			// Force PlayCanvas to compute the standard projection matrix.
			// Since projMat IS pcCamera._projMat, reading the getter fills it
			// with the correct default perspective/ortho values.
			void camera.projectionMatrix; // eslint-disable-line @typescript-eslint/no-unused-expressions

			// Restore our override for subsequent calls in this frame
			camera._calculateProjection = callback;

			// Apply sub-pixel offset to the projection matrix.
			// Elements [8] and [9] shift the image in X and Y (NDC space).
			projMat.data[8] += (2.0 * jitterX) / device.width;
			projMat.data[9] += (2.0 * jitterY) / device.height;
		};

		return callback;
	}

	private _clearJitter() {
		const { camera } = this._cameraComponent;
		camera._calculateProjection = null;
		camera._projMatDirty = true;
	}
}

// Halton sequence for quasi-random sub-pixel jitter offsets.
function halton(index: number, base: number): number {
	let result = 0;
	let f = 1 / base;
	let i = index;
	while (i > 0) {
		result += f * (i % base);
		i = Math.floor(i / base);
		f /= base;
	}
	return result;
}

/**
 * Post-effect that blends the current rendered frame with a previous
 * accumulation texture, and stores the result for the next iteration.
 *
 * On each frame it does two draw calls:
 *   1. Blend input + previous accumulation → write to internal accumulation RT
 *   2. Copy the accumulation RT → output (screen or next effect)
 */
class AccumulationEffect extends PostEffect {
	blendFactor = 0.0;

	private _blendShader!: Shader;
	private _copyShader!: Shader;
	// Ping-pong pair: avoids read-write hazard (can't read and write the same RT)
	private _rtA: RenderTarget | null = null;
	private _rtB: RenderTarget | null = null;
	private _writeToA = true;
	private _onResize: () => void;

	constructor(device: GraphicsDevice, onResize: () => void) {
		super(device);
		this.needsDepthBuffer = false;
		this._onResize = onResize;
		this._createShaders();
	}

	render(inputTarget: RenderTarget, outputTarget: RenderTarget, rect: Vec4) {
		this._ensureRTs(inputTarget);

		const readRT = this._writeToA ? this._rtB! : this._rtA!;
		const writeRT = this._writeToA ? this._rtA! : this._rtB!;
		const scope = this.device.scope;

		scope.resolve("uColorBuffer").setValue(inputTarget.colorBuffer);
		scope.resolve("uAccumBuffer").setValue(readRT.colorBuffer);
		scope.resolve("uBlendFactor").setValue(this.blendFactor);
		this.drawQuad(writeRT, this._blendShader, rect);

		scope.resolve("uTexture").setValue(writeRT.colorBuffer);
		this.drawQuad(outputTarget, this._copyShader, rect);

		// Swap for next frame
		this._writeToA = !this._writeToA;
	}

	destroy(): void {
		this._destroyRTs();
		this._blendShader.destroy();
		this._copyShader.destroy();
	}

	private _ensureRTs(reference: RenderTarget) {
		const refTex = reference.colorBuffer as Texture;
		if (
			this._rtA &&
			this._rtA.colorBuffer.width === refTex.width &&
			this._rtA.colorBuffer.height === refTex.height
		) {
			return;
		}

		if (this._rtA) {
			this._onResize();
		}

		this._destroyRTs();

		const createRT = (name: string) =>
			new RenderTarget({
				colorBuffer: new Texture(this.device, {
					name,
					width: refTex.width,
					height: refTex.height,
					format: PIXELFORMAT_R8_G8_B8_A8,
					minFilter: FILTER_NEAREST,
					magFilter: FILTER_NEAREST,
					addressU: ADDRESS_CLAMP_TO_EDGE,
					addressV: ADDRESS_CLAMP_TO_EDGE,
					mipmaps: false,
				}),
				depth: false,
			});

		this._rtA = createRT("PAA_PingA");
		this._rtB = createRT("PAA_PingB");
		this._writeToA = true;
		this.blendFactor = 0.0;
	}

	private _destroyRTs() {
		for (const rt of [this._rtA, this._rtB]) {
			if (rt) {
				(rt.colorBuffer as Texture).destroy();
				rt.destroy();
			}
		}
		this._rtA = null;
		this._rtB = null;
	}

	private _createShaders() {
		this._blendShader = createQuadShader(this.device, {
			name: "ConfiguratorProgressiveAA_blend",
			fragment: BLEND_SHADER,
		});
		this._copyShader = createQuadShader(this.device, {
			name: "ConfiguratorProgressiveAA_copy",
			fragment: COPY_SHADER,
		});
	}
}
